import { useCancelWorkOrderComplement } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { ErrorFieldErrors } from '@/core/request/ErrorResponse';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
  useDisclosure,
} from '@/hooks';
import { Alert, Button, Divider, Form, Input, List, Modal } from 'antd';
import { AxiosError } from 'axios';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { buildParamsStr } from '@/utils/queryParams';

const ComplementCancel: React.FC = () => {
  const {
    workorder: { data: workorder },
    complement: { data: complement },
    isPortocarWithClosedOrCancelled,
  } = useWorkorderDetailContext();

  const defaultRule = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();
  const hasPecaAiBasket = !!complement?.pecaAiBasketId;

  const [form] = Form.useForm<{ cancellationReason: string }>();

  const [modalOpened, modalAction] = useDisclosure();

  const cancelComplement = useCancelWorkOrderComplement();

  const redirectToReturnMaterialPage = () => {
    const workorderNumber = String(workorder?.number).padStart(6, '0');
    const complementNumber = String(complement?.number).padStart(2, '0');
    const search = buildParamsStr({
      companyId: workorder?.company.id,
      search: `${workorderNumber}/${complementNumber}`,
    });

    window.open(
      `/app/movements/deliveryOfMaterials/returnMaterial?${search}`,
      '_blank',
    );
  };

  async function cancelOS(cancellationReason: string) {
    try {
      if (workorder && complement) {
        await cancelComplement.mutateAsync({
          workorderId: workorder.id,
          complementId: complement.id,
          cancellationReason,
        });

        modalAction.close();
      }
    } catch (error: unknown) {
      const err = error as AxiosError<ErrorFieldErrors>;

      if (
        err.isAxiosError &&
        err.response?.data &&
        err.response?.data?.fieldErrors &&
        err.response.status === 400
      ) {
        const { response } = err;

        Modal.info({
          icon: null,
          title: 'Não foi possível cancelar o orçamento',
          okText: 'Confirmar',
          width: 800,
          content: (
            <>
              {!!response.data.fieldErrors?.invoice?.length && (
                <Alert
                  style={{ marginTop: 24 }}
                  type="warning"
                  message={<strong>Nota Fiscal</strong>}
                  description="Existe nota fiscal, faça a exclusão ou cancelamento antes de
                              cancelar a O.S."
                />
              )}

              {!!response.data.fieldErrors?.financial?.length && (
                <Alert
                  style={{ marginTop: 24 }}
                  type="warning"
                  message={<strong>Financeiro</strong>}
                  description="Existe financeiro referente a este orçamento."
                />
              )}

              {!!response.data.fieldErrors?.purchase?.length && (
                <>
                  <Divider orientation="left" style={{ fontWeight: 'bold' }}>
                    Existem compra(s) referentes aos itens
                  </Divider>

                  <List
                    bordered
                    dataSource={response.data.fieldErrors?.purchase}
                    renderItem={(item) => <List.Item>{item}</List.Item>}
                  />
                </>
              )}

              {!!response.data.fieldErrors?.returnedItems?.length && (
                <>
                  <Divider orientation="left" style={{ fontWeight: 'bold' }}>
                    Itens a serem devolvidos
                  </Divider>

                  <List
                    bordered
                    dataSource={response.data.fieldErrors.returnedItems}
                    renderItem={(item) => <List.Item>{item}</List.Item>}
                    footer={
                      <Button
                        type="link"
                        onClick={redirectToReturnMaterialPage}
                      >
                        Fazer devolução dos itens
                      </Button>
                    }
                  />
                </>
              )}
            </>
          ),
        });
      }
    }
  }

  async function handleSubmit() {
    try {
      const { cancellationReason } = await form.validateFields();

      await cancelOS(cancellationReason);
    } catch {}
  }

  return (
    <>
      <Button
        block
        type="ghost"
        onClick={modalAction.open}
        danger
        disabled={
          !hasPermissions([Authorities.MOVIMENTATION_WORKORDER_CANCEL]) ||
          isPortocarWithClosedOrCancelled
        }
      >
        Cancelar OS
      </Button>

      <Modal
        title="Cancelar orçamento"
        open={modalOpened}
        onCancel={modalAction.close}
        onOk={handleSubmit}
        destroyOnClose
        okText="Cancelar"
        cancelText="Fechar"
        confirmLoading={cancelComplement.isLoading}
      >
        <Form form={form} preserve={false} layout="vertical">
          <Form.Item
            label="Motivo do cancelamento"
            rules={[defaultRule, defaultMaxLenghtFieldError(100)]}
            name="cancellationReason"
          >
            <Input.TextArea rows={5} />
          </Form.Item>

          {hasPecaAiBasket && (
            <Alert
              type="error"
              message={<strong>Importante</strong>}
              description={
                <>
                  <p>
                    Este orçamento possui pedido ativo no{' '}
                    <strong>Peça Aí</strong>.
                  </p>

                  <p>
                    O cancelamento aqui no sistema não cancela o pedido
                    automaticamente.
                  </p>

                  <p>
                    Acesse o <strong>Peça Aí</strong> e realize o cancelamento
                    para evitar faturamento ou envio indevido das peças.
                  </p>

                  <a
                    href="https://loja.pecaai.com.br/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      block
                      type="primary"
                      danger
                      style={{ marginTop: 8 }}
                    >
                      Ir para o Peça Aí
                    </Button>
                  </a>
                </>
              }
            />
          )}
        </Form>
      </Modal>
    </>
  );
};

export default ComplementCancel;
